/**
 * CallFlow List Component
 *
 * Displays all CallFlows with status badges, filtering, and quick actions.
 */

import { useEffect, useState } from 'react';
import { callFlowService, CallFlow } from '@/services/callFlowService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import {
  Play,
  Pause,
  Trash2,
  Eye,
  Loader2,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { CreateCallFlowDialog } from './CreateCallFlowDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface CallFlowListProps {
  organizationId: string;
  onViewDetails: (callflowId: string) => void;
}

export function CallFlowList({ organizationId, onViewDetails }: CallFlowListProps) {
  const [callflows, setCallflows] = useState<CallFlow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [callflowToDelete, setCallflowToDelete] = useState<CallFlow | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchCallFlows();
  }, [statusFilter]);

  const fetchCallFlows = async () => {
    try {
      setIsLoading(true);
      const filters = statusFilter !== 'all' ? { status: statusFilter } : {};
      const { callflows: data } = await callFlowService.listCallFlows(organizationId, filters);
      setCallflows(data);
    } catch (error) {
      console.error('Failed to fetch CallFlows:', error);
      toast({
        title: 'Error',
        description: 'Failed to load CallFlows. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStart = async (callflow: CallFlow) => {
    try {
      setActionLoading(callflow.id);
      await callFlowService.startCallFlow(callflow.id);
      toast({
        title: 'Success',
        description: `CallFlow "${callflow.name}" has been queued for execution.`,
      });
      await fetchCallFlows();
    } catch (error: any) {
      console.error('Failed to start CallFlow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async (callflow: CallFlow) => {
    try {
      setActionLoading(callflow.id);
      await callFlowService.pauseCallFlow(callflow.id);
      toast({
        title: 'Success',
        description: `CallFlow "${callflow.name}" is pausing. Active calls will complete gracefully.`,
      });
      await fetchCallFlows();
    } catch (error: any) {
      console.error('Failed to pause CallFlow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to pause CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (callflow: CallFlow) => {
    try {
      setActionLoading(callflow.id);
      await callFlowService.resumeCallFlow(callflow.id);
      toast({
        title: 'Success',
        description: `CallFlow "${callflow.name}" has been queued to resume.`,
      });
      await fetchCallFlows();
    } catch (error: any) {
      console.error('Failed to resume CallFlow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to resume CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDelete = (callflow: CallFlow) => {
    setCallflowToDelete(callflow);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!callflowToDelete) return;

    try {
      setActionLoading(callflowToDelete.id);
      await callFlowService.deleteCallFlow(organizationId, callflowToDelete.id);
      toast({
        title: 'Success',
        description: `CallFlow "${callflowToDelete.name}" has been deleted.`,
      });
      await fetchCallFlows();
    } catch (error: any) {
      console.error('Failed to delete CallFlow:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete CallFlow',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
      setDeleteDialogOpen(false);
      setCallflowToDelete(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      draft: 'secondary',
      active: 'default',
      paused: 'outline',
      completed: 'default',
      completed_with_failures: 'outline',
      cancelled: 'destructive',
      failed: 'destructive',
    };

    const colors: Record<string, string> = {
      draft: 'bg-muted text-foreground',
      active: 'bg-good-bg text-good',
      paused: 'bg-mid-bg text-mid',
      completed: 'bg-secondary text-foreground',
      completed_with_failures: 'bg-mid-bg text-mid',
      cancelled: 'bg-danger-bg text-danger',
      failed: 'bg-danger-bg text-danger',
    };

    return (
      <Badge variant={variants[status] || 'default'} className={colors[status]}>
        {status.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const canStart = (callflow: CallFlow) => {
    return ['draft', 'paused'].includes(callflow.status);
  };

  const canPause = (callflow: CallFlow) => {
    return callflow.status === 'active';
  };

  const canResume = (callflow: CallFlow) => {
    return callflow.status === 'paused';
  };

  const canDelete = (callflow: CallFlow) => {
    return callflow.status !== 'active';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">CallFlows</h1>
          <p className="text-muted-foreground mt-1">
            Automated batch calling campaigns with retry logic
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchCallFlows}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create CallFlow
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium">Status:</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="completed_with_failures">Completed with Failures</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* CallFlows List */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-ink-faint" />
        </div>
      ) : callflows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {statusFilter === 'all'
                ? 'No CallFlows found. Create your first CallFlow to get started.'
                : `No CallFlows with status "${statusFilter}".`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {callflows.map((callflow) => (
            <Card key={callflow.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <CardTitle>{callflow.name}</CardTitle>
                      {getStatusBadge(callflow.status)}
                    </div>
                    {callflow.description && (
                      <p className="text-muted-foreground text-sm mt-2">{callflow.description}</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Leads</p>
                    <p className="text-2xl font-bold">{callflow.total_leads}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold text-good">
                      {callflow.calls_completed}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold text-danger">
                      {callflow.calls_failed}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">In Progress</p>
                    <p className="text-2xl font-bold text-foreground">
                      {callflow.calls_in_progress}
                    </p>
                  </div>
                </div>

                {/* Progress Bar */}
                {callflow.total_leads > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        {Math.round(
                          ((callflow.calls_completed + callflow.calls_failed) /
                            callflow.total_leads) *
                            100
                        )}
                        %
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{
                          width: `${
                            ((callflow.calls_completed + callflow.calls_failed) /
                              callflow.total_leads) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewDetails(callflow.id)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </Button>

                  {canStart(callflow) && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleStart(callflow)}
                      disabled={actionLoading === callflow.id}
                    >
                      {actionLoading === callflow.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Start
                    </Button>
                  )}

                  {canPause(callflow) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePause(callflow)}
                      disabled={actionLoading === callflow.id}
                    >
                      {actionLoading === callflow.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Pause className="h-4 w-4 mr-2" />
                      )}
                      Pause
                    </Button>
                  )}

                  {canResume(callflow) && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleResume(callflow)}
                      disabled={actionLoading === callflow.id}
                    >
                      {actionLoading === callflow.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Resume
                    </Button>
                  )}

                  {canDelete(callflow) && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => confirmDelete(callflow)}
                      disabled={actionLoading === callflow.id}
                    >
                      {actionLoading === callflow.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <CreateCallFlowDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={fetchCallFlows}
        organizationId={organizationId}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete CallFlow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{callflowToDelete?.name}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
